import React, { useEffect, useState } from 'react';
import styles from './TrendingProjectsSection.module.scss';
import Divider from 'components/divider/Divider';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Card } from 'antd';
import { Link } from 'react-router-dom';
import { ProjectService } from '../../../services/ProjectService';
import type { Project } from '../../../types/Projects';
import { UnorderedListOutlined, AppstoreOutlined } from '@ant-design/icons';

const TrendingProjectsSection = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await ProjectService.getProjects(1, 10);
        setProjects(res.items);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleViewModeChange = (mode: 'list' | 'grid') => {
    if (viewMode === mode) return;

    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode(mode);
      setIsTransitioning(false);
    }, 100);
  };

  return (
    <div className={styles.trendingProjects}>
      <div className={styles.header}>
        <h2>{t('TrendingProjectsHeading')}</h2>
        <div className={styles.toggleIcons}>
          <Button
            type={viewMode === 'list' ? 'primary' : 'default'}
            shape='circle'
            icon={<UnorderedListOutlined />}
            onClick={() => handleViewModeChange('list')}
            className={styles.viewToggleBtn}
            style={viewMode === 'list' ? { backgroundColor: '#52bf92', borderColor: '#52bf92' } : {}}
          />
          <Button
            type={viewMode === 'grid' ? 'primary' : 'default'}
            shape='circle'
            icon={<AppstoreOutlined />}
            onClick={() => handleViewModeChange('grid')}
            className={styles.viewToggleBtn}
            style={viewMode === 'grid' ? { backgroundColor: '#52bf92', borderColor: '#52bf92' } : {}}
          />
        </div>
      </div>

      <Divider />

      <div className={`${styles.tableContainer} ${isTransitioning ? styles.transitioning : ''}`}>
        {loading ? (
          <Spin size='large' />
        ) : projects.length === 0 ? (
          <p>No Project Found</p>
        ) : viewMode === 'list' ? (
          // LIST VIEW
          <table className={styles.listView}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('ProjectName')}</th>
                <th>{t('ProjectType')}</th>
                <th>{t('Emission-Reductions')}</th>
                <th>{t('Standard')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr key={project.id}>
                  <td>{index + 1}</td>
                  <td>{project.name}</td>
                  <td>{t(project.projectType)}</td>
                  <td>{project.emissionReductions}</td>
                  <td>{project.standard || '-'}</td>
                  <td>
                    <Link to={`/ProjectDetails/${project.id}`}>
                      <Button className={styles.buttonColor} type='primary'>
                        {t('View')}
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          // GRID / TILE VIEW
          <div className={styles.grid}>
            {projects.map((project) => (
              <div key={project.id} className={styles.cardWrapper}>
                <Card
                  hoverable
                  cover={
                    project.coverImage ? (
                      <div className={styles.imageContainer}>
                        <img alt={project.name} src={project.coverImage} className={styles.projectImage} />
                        <div className={styles.projectTypeBadge}>{t(project.projectType)}</div>
                      </div>
                    ) : (
                      <div className={styles.placeholderImage}>
                        <div className={styles.projectTypeBadge}>{t(project.projectType)}</div>
                      </div>
                    )
                  }
                  className={styles.projectCard}
                  bodyStyle={{ padding: '16px' }}
                >
                  <div className={styles.cardContent}>
                    <h3 className={styles.projectTitle}>{project.name}</h3>

                    <div className={styles.projectStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>{t('EmissionReductions')}</span>
                        <span className={styles.statValue}>{project.emissionReductions}</span>
                      </div>

                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>{t('Standard')}</span>
                        <span className={styles.statValue}>{project.standard || '-'}</span>
                      </div>
                    </div>

                    <div className={styles.cardActions}>
                      <Link to={`/ProjectDetails/${project.id}`}>
                        <Button className={styles.buttonColor} type='primary' block>
                          {t('Click Here')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendingProjectsSection;
